import { useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Divider,
  Box,
  Icon,
} from '@shopify/polaris';
import type { IconSource } from '@shopify/polaris';
import {
  HomeIcon,
  OrderIcon,
  ProductIcon,
  DeliveryIcon,
  RefreshIcon,
  ChartVerticalIcon,
  SettingsIcon,
  QuestionCircleIcon,
  CheckCircleIcon,
  AlertCircleIcon,
  InventoryIcon,
  CodeIcon,
  AutomationIcon,
  GlobeIcon,
} from '@shopify/polaris-icons';

// ============================================================
// HELP CONTENT SECTIONS
// ============================================================

interface HelpSection {
  id: string;
  title: string;
  icon: IconSource;
  content: React.ReactNode;
}

const helpSections: HelpSection[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: HomeIcon,
    content: (
      <BlockStack gap="400">
        <Text as="h2" variant="headingLg">Getting Started with Whenly</Text>
        <Text as="p">
          Welcome to Whenly, your complete delivery date and warehouse management solution for Shopify.
          This guide will help you get up and running quickly.
        </Text>

        <Text as="h3" variant="headingMd">Initial Setup</Text>
        <BlockStack gap="200">
          <InlineStack gap="200" blockAlign="start">
            <Box><Icon source={CheckCircleIcon} tone="success" /></Box>
            <BlockStack gap="100">
              <Text as="span" fontWeight="semibold">1. Connect Your Shopify Store</Text>
              <Text as="p" tone="subdued">
                After signing up, you'll be prompted to connect your Shopify store. Click "Connect Store"
                and authorise Whenly to access your store data.
              </Text>
            </BlockStack>
          </InlineStack>
          <InlineStack gap="200" blockAlign="start">
            <Box><Icon source={CheckCircleIcon} tone="success" /></Box>
            <BlockStack gap="100">
              <Text as="span" fontWeight="semibold">2. Import Your Data</Text>
              <Text as="p" tone="subdued">
                Go to Settings → Data Import to sync your products and orders from Shopify.
                This typically takes a few minutes depending on your catalogue size.
              </Text>
            </BlockStack>
          </InlineStack>
          <InlineStack gap="200" blockAlign="start">
            <Box><Icon source={CheckCircleIcon} tone="success" /></Box>
            <BlockStack gap="100">
              <Text as="span" fontWeight="semibold">3. Configure Delivery Zones</Text>
              <Text as="p" tone="subdued">
                Set up your delivery zones in Configuration → Delivery Zones. Define areas by
                postcode, region, or country with specific lead times and capacity.
              </Text>
            </BlockStack>
          </InlineStack>
          <InlineStack gap="200" blockAlign="start">
            <Box><Icon source={CheckCircleIcon} tone="success" /></Box>
            <BlockStack gap="100">
              <Text as="span" fontWeight="semibold">4. Set Up Warehouses</Text>
              <Text as="p" tone="subdued">
                Create your warehouse locations in Warehouse → Warehouses. You can have multiple
                warehouses with different stock levels and fulfilment capabilities.
              </Text>
            </BlockStack>
          </InlineStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Key Concepts</Text>
        <BlockStack gap="300">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Organisations</Text>
            <Text as="p" tone="subdued">
              Your company account that contains all your stores, team members, and settings.
              Each organisation has its own subdomain (e.g., yourcompany.whenlyapp.com).
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Stores</Text>
            <Text as="p" tone="subdued">
              Connected Shopify stores. Depending on your plan, you can connect multiple stores
              to a single organisation.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Team Members</Text>
            <Text as="p" tone="subdued">
              Invite colleagues with different roles: Admin, Manager, Picker, or Viewer.
              Each role has different permissions.
            </Text>
          </BlockStack>
        </BlockStack>
      </BlockStack>
    ),
  },
  {
    id: 'dashboard',
    title: 'Dashboard',
    icon: HomeIcon,
    content: (
      <BlockStack gap="400">
        <Text as="h2" variant="headingLg">Dashboard Overview</Text>
        <Text as="p">
          The dashboard provides a quick overview of your business operations at a glance.
        </Text>

        <Text as="h3" variant="headingMd">Key Metrics</Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Today's Orders</Text>
            <Text as="p" tone="subdued">
              Shows the number of orders received today along with comparison to yesterday.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Pending Fulfilment</Text>
            <Text as="p" tone="subdued">
              Orders that have been paid but not yet shipped or delivered.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Delivery Routes</Text>
            <Text as="p" tone="subdued">
              Active delivery routes for today with status (planned, in progress, completed).
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Low Stock Alerts</Text>
            <Text as="p" tone="subdued">
              Products that have fallen below your configured low stock threshold.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Quick Actions</Text>
        <Text as="p" tone="subdued">
          The dashboard provides quick access to common tasks:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li>Create a new delivery route</li>
          <li>View today's orders</li>
          <li>Check pending returns</li>
          <li>Access pick lists</li>
        </ul>
      </BlockStack>
    ),
  },
  {
    id: 'orders',
    title: 'Orders Management',
    icon: OrderIcon,
    content: (
      <BlockStack gap="400">
        <Text as="h2" variant="headingLg">Orders Management</Text>
        <Text as="p">
          Manage all your Shopify orders from one place with advanced filtering and bulk actions.
        </Text>

        <Text as="h3" variant="headingMd">Order Tabs</Text>
        <Text as="p" tone="subdued">
          The Orders page has quick-access tabs for common views:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li><strong>All Orders:</strong> Complete list of all orders</li>
          <li><strong>Today's Deliveries:</strong> Orders scheduled for delivery today (links to shipping calendar)</li>
          <li><strong>Pending Allocation:</strong> Orders waiting to be allocated to stock</li>
          <li><strong>Backorder:</strong> Orders with items that cannot be fulfilled due to insufficient stock</li>
          <li><strong>In Picking:</strong> Orders currently being picked in the warehouse</li>
        </ul>

        <Divider />

        <Text as="h3" variant="headingMd">Filtering Orders</Text>
        <Text as="p" tone="subdued">
          Use the filters to narrow down orders by:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li><strong>Status:</strong> Pending, Allocated, Picking, Dispatched, Delivered, Cancelled</li>
          <li><strong>Delivery Date:</strong> Filter by scheduled delivery date or use the date picker</li>
          <li><strong>Search:</strong> Search by order number or customer name</li>
        </ul>
        <Text as="p" tone="subdued">
          You can also click dates on the Shipping Calendar to view orders for that specific day.
        </Text>

        <Divider />

        <Text as="h3" variant="headingMd">Order Details</Text>
        <Text as="p" tone="subdued">
          Click on any order to view full details including:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li>Customer information and shipping address</li>
          <li>Line items with product details and quantities</li>
          <li>Selected delivery date (if applicable)</li>
          <li>Order notes and tags</li>
          <li>Fulfilment history</li>
          <li>Return requests associated with the order</li>
        </ul>

        <Divider />

        <Text as="h3" variant="headingMd">Order Actions</Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Add to Route</Text>
            <Text as="p" tone="subdued">
              Add an order to an existing delivery route or create a new one.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Create Pick List</Text>
            <Text as="p" tone="subdued">
              Generate a pick list for warehouse staff to fulfil the order.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Print Packing Slip</Text>
            <Text as="p" tone="subdued">
              Generate and print a packing slip to include with the shipment.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Merge Orders</Text>
            <Text as="p" tone="subdued">
              Combine multiple orders from the same customer into a single delivery.
              (See Automation & Rules section for details.)
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Split Order</Text>
            <Text as="p" tone="subdued">
              Split a partially allocated order into two separate orders. Part 1 contains items
              that are allocated and ready for delivery, Part 2 contains items still pending stock.
              This allows partial fulfilment while waiting for remaining items.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Print Delivery Note</Text>
            <Text as="p" tone="subdued">
              Print the order details or save them as a PDF. Orders with a tracking link include a QR code.
              (See Barcodes & Printing section for details.)
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Order Timeline</Text>
        <Text as="p" tone="subdued">
          Every order has a complete audit log showing all events: creation, allocation,
          picking, packing, dispatch, and delivery. See who performed each action and when.
          (See Automation & Rules section for details.)
        </Text>

        <Divider />

        <Text as="h3" variant="headingMd">Split Orders</Text>
        <Text as="p" tone="subdued">
          When an order has some items allocated (in stock) and others pending allocation (out of stock),
          you can split it into two separate orders for partial fulfilment.
        </Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">When to Split</Text>
            <Text as="p" tone="subdued">
              The "Split Order" option appears when an order has mixed allocation - some items
              are ready to ship while others are awaiting stock.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">How It Works</Text>
            <Text as="p" tone="subdued">
              Click "Split Order" to preview the split. Part 1 will contain all allocated items
              ready for immediate despatch. Part 2 will contain unallocated items that remain
              pending until stock arrives.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Partial Items</Text>
            <Text as="p" tone="subdued">
              If an item is partially allocated (e.g., 2 of 5 units allocated), it will be split
              between both orders - allocated units go to Part 1, remaining units to Part 2.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Order Numbers</Text>
            <Text as="p" tone="subdued">
              Split orders get new order numbers with "-P1" and "-P2" suffixes (e.g., #1234-P1 and #1234-P2).
              The original order is marked as split with a link to both parts.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Manual Orders</Text>
        <Text as="p" tone="subdued">
          Create orders directly in Whenly without them originating from Shopify.
        </Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Creating a Manual Order</Text>
            <Text as="p" tone="subdued">
              Go to Orders and click "Create Manual Order". Enter customer details, add products
              and quantities, then save the order.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Use Cases</Text>
            <Text as="p" tone="subdued">
              Manual orders are useful for phone orders, wholesale orders, internal transfers,
              or any orders placed outside your Shopify store.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Manual vs Shopify Orders</Text>
            <Text as="p" tone="subdued">
              Manual orders are tagged as "Manual" and have order numbers starting with "M".
              They follow the same workflow as Shopify orders but are not synced back to Shopify.
            </Text>
          </BlockStack>
        </BlockStack>
      </BlockStack>
    ),
  },
  {
    id: 'products',
    title: 'Products & Inventory',
    icon: ProductIcon,
    content: (
      <BlockStack gap="400">
        <Text as="h2" variant="headingLg">Products & Inventory</Text>
        <Text as="p">
          Manage your product catalogue and track inventory across multiple warehouses.
        </Text>

        <Text as="h3" variant="headingMd">Products</Text>
        <Text as="p" tone="subdued">
          The Products section shows all products synced from your Shopify store. You can:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li>Search by product name, SKU, or barcode</li>
          <li>Filter by vendor, product type, or collection</li>
          <li>View and edit product delivery settings</li>
          <li>Set product-specific lead times</li>
          <li>Configure alternative products for substitutions</li>
        </ul>

        <Divider />

        <Text as="h3" variant="headingMd">Warehouses</Text>
        <Text as="p" tone="subdued">
          Set up multiple warehouse locations with:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li>Unique names and addresses</li>
          <li>Contact information</li>
          <li>Operating hours</li>
          <li>Priority for stock allocation</li>
        </ul>

        <Divider />

        <Text as="h3" variant="headingMd">Inventory Management</Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Stock Levels</Text>
            <Text as="p" tone="subdued">
              View real-time stock levels across all warehouses. Adjust quantities manually or via bulk import.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Stock Transfers</Text>
            <Text as="p" tone="subdued">
              Move stock between warehouses with full audit trail and tracking.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Stock Takes</Text>
            <Text as="p" tone="subdued">
              Conduct periodic stock counts to verify inventory accuracy. Compare counted vs system quantities.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Low Stock Alerts</Text>
            <Text as="p" tone="subdued">
              Set thresholds for automatic alerts when stock falls below minimum levels.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Serial Number Tracking</Text>
            <Text as="p" tone="subdued">
              Track individual units by serial number. View complete lifecycle history.
              (See Warehouse Operations section for details.)
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Transfer Orders</Text>
            <Text as="p" tone="subdued">
              Move stock between warehouses with full tracking and Shopify sync.
              (See Warehouse Operations section for details.)
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Damage Reports</Text>
            <Text as="p" tone="subdued">
              Document damaged goods with photos, quarantine items, and process resolutions.
              (See Warehouse Operations section for details.)
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Pick Lists</Text>
        <Text as="p" tone="subdued">
          Generate pick lists for efficient warehouse picking:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li>Group items by location for efficient picking routes</li>
          <li>Print or view on mobile devices</li>
          <li>Mark items as picked in real-time</li>
          <li>Handle partial picks and substitutions</li>
        </ul>
      </BlockStack>
    ),
  },
  {
    id: 'delivery',
    title: 'Delivery & Shipping',
    icon: DeliveryIcon,
    content: (
      <BlockStack gap="400">
        <Text as="h2" variant="headingLg">Delivery & Shipping</Text>
        <Text as="p">
          Configure delivery options and manage shipping for your customers.
        </Text>

        <Text as="h3" variant="headingMd">Delivery Zones</Text>
        <Text as="p" tone="subdued">
          Define geographical areas where you deliver:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li>Create zones by postcode, region, or country</li>
          <li>Set specific lead times per zone</li>
          <li>Configure delivery fees per zone</li>
          <li>Enable/disable zones seasonally</li>
        </ul>

        <Divider />

        <Text as="h3" variant="headingMd">Delivery Capacity</Text>
        <Text as="p" tone="subdued">
          Control how many deliveries you can handle per day:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li>Set daily capacity limits per zone</li>
          <li>Configure different capacities by day of week</li>
          <li>Automatically close dates when capacity is reached</li>
          <li>View capacity utilisation on the calendar</li>
        </ul>

        <Divider />

        <Text as="h3" variant="headingMd">Blocked Dates</Text>
        <Text as="p" tone="subdued">
          Block specific dates when you cannot deliver:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li>Public holidays</li>
          <li>Company closures</li>
          <li>Maintenance periods</li>
          <li>Recurring weekly blocks (e.g., Sundays)</li>
        </ul>

        <Divider />

        <Text as="h3" variant="headingMd">Delivery Routes</Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Creating Routes</Text>
            <Text as="p" tone="subdued">
              Build routes manually or use the Route Builder to automatically group orders by location.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Route Optimisation</Text>
            <Text as="p" tone="subdued">
              Optimise stop order to minimise travel time and distance.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Driver Assignment</Text>
            <Text as="p" tone="subdued">
              Assign routes to drivers and track their progress in real-time.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Proof of Delivery</Text>
            <Text as="p" tone="subdued">
              Capture signatures and photos as proof of delivery.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Weight Rules</Text>
        <Text as="p" tone="subdued">
          Configure delivery restrictions based on order weight:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li>Set maximum weight per delivery</li>
          <li>Apply surcharges for heavy orders</li>
          <li>Restrict delivery methods by weight</li>
        </ul>
      </BlockStack>
    ),
  },
  {
    id: 'calendar',
    title: 'Calendar & Scheduling',
    icon: HomeIcon,
    content: (
      <BlockStack gap="400">
        <Text as="h2" variant="headingLg">Calendar & Scheduling</Text>
        <Text as="p">
          Visualise and manage your delivery schedule with the shipping calendar.
        </Text>

        <Text as="h3" variant="headingMd">Shipping Calendar</Text>
        <Text as="p" tone="subdued">
          The calendar view shows:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li>Number of orders scheduled per day (by delivery or ship date)</li>
          <li>Click any date to view orders scheduled for that day</li>
          <li>Toggle between Ship Date and Delivery Date views</li>
          <li>Summary cards showing orders without dates and overdue orders</li>
        </ul>

        <Text as="h3" variant="headingMd">Today's Deliveries</Text>
        <Text as="p" tone="subdued">
          The Orders page includes a "Today's Deliveries" tab that shows all orders scheduled for delivery today. This tab:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li>Automatically filters to today's date</li>
          <li>Links directly from the shipping calendar when clicking today</li>
          <li>Shows only orders with a delivery date matching today</li>
        </ul>

        <Text as="h3" variant="headingMd">Calendar Summary Cards</Text>
        <Text as="p" tone="subdued">
          Quick action cards at the bottom of the calendar:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li><strong>Orders without Delivery dates:</strong> Click to view orders that haven't had a delivery date assigned</li>
          <li><strong>Orders overdue for delivery:</strong> Click to view orders past their scheduled delivery date</li>
        </ul>

        <Divider />

        <Text as="h3" variant="headingMd">Delivery Rules</Text>
        <Text as="p" tone="subdued">
          Configure rules that determine available delivery dates:
        </Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Lead Time</Text>
            <Text as="p" tone="subdued">
              Minimum days notice required before a delivery date is available.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Cutoff Time</Text>
            <Text as="p" tone="subdued">
              Daily cutoff time after which orders count towards the next day's lead time.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Available Days</Text>
            <Text as="p" tone="subdued">
              Which days of the week deliveries are available (e.g., exclude Sundays).
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Advance Booking</Text>
            <Text as="p" tone="subdued">
              Maximum days in advance customers can book a delivery.
            </Text>
          </BlockStack>
        </BlockStack>
      </BlockStack>
    ),
  },
  {
    id: 'returns',
    title: 'Returns Management',
    icon: RefreshIcon,
    content: (
      <BlockStack gap="400">
        <Text as="h2" variant="headingLg">Returns Management</Text>
        <Text as="p">
          Handle customer returns and supplier returns efficiently.
        </Text>

        <Text as="h3" variant="headingMd">Customer Returns</Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Return Requests</Text>
            <Text as="p" tone="subdued">
              View and process return requests from customers. Each request shows the order,
              items to return, and reason provided.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Return Workflow</Text>
            <Text as="p" tone="subdued">
              Returns follow a workflow: Pending → Approved/Rejected → Received → Restocked/Written Off
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Return Reasons</Text>
            <Text as="p" tone="subdued">
              Configure return reasons (e.g., Damaged, Wrong Item, Changed Mind) in Returns → Return Reasons.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Photos</Text>
            <Text as="p" tone="subdued">
              Customers can attach photos to document the condition of returned items.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Processing Returns</Text>
        <ol style={{ marginLeft: '20px', color: '#637381' }}>
          <li><strong>Review:</strong> Check the return request and photos</li>
          <li><strong>Approve/Reject:</strong> Approve valid returns or reject with reason</li>
          <li><strong>Receive:</strong> Mark as received when item arrives at warehouse</li>
          <li><strong>Inspect:</strong> Check item condition</li>
          <li><strong>Restock/Write Off:</strong> Return to inventory or mark as damaged/unsellable</li>
        </ol>

        <Divider />

        <Text as="h3" variant="headingMd">Supplier Returns</Text>
        <Text as="p" tone="subdued">
          Manage returns to your suppliers:
        </Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Supplier Invoices</Text>
            <Text as="p" tone="subdued">
              Record supplier invoices to track what was purchased and from whom.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Return Batches</Text>
            <Text as="p" tone="subdued">
              Create batches of items to return to a supplier. Track credit notes and refunds.
            </Text>
          </BlockStack>
        </BlockStack>
      </BlockStack>
    ),
  },
  {
    id: 'reports',
    title: 'Reports & Analytics',
    icon: ChartVerticalIcon,
    content: (
      <BlockStack gap="400">
        <Text as="h2" variant="headingLg">Reports & Analytics</Text>
        <Text as="p">
          Gain insights into your operations with comprehensive reporting.
        </Text>

        <Text as="h3" variant="headingMd">Available Reports</Text>
        <BlockStack gap="300">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Backorder Report</Text>
            <Text as="p" tone="subdued">
              Shows all order items where the ordered quantity exceeds the allocated quantity.
              These are items on active orders (not cancelled, delivered, or dispatched) that
              cannot be fulfilled due to insufficient stock. Use this report to identify
              which SKUs need reordering and prioritise purchasing decisions.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Product Flow Report</Text>
            <Text as="p" tone="subdued">
              Track product movement: incoming stock, outgoing orders, returns, adjustments.
              Useful for understanding product velocity.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Stock Valuation Report</Text>
            <Text as="p" tone="subdued">
              Calculate the total value of your inventory across all warehouses.
              Group by vendor, product type, or location.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Delivery Performance Report</Text>
            <Text as="p" tone="subdued">
              Analyse delivery metrics: on-time rate, average delivery time, failed deliveries,
              delivery costs per zone.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Sales Velocity Report</Text>
            <Text as="p" tone="subdued">
              See how fast products are selling. Identify fast movers and slow movers
              to optimise inventory levels.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Picker Productivity Report</Text>
            <Text as="p" tone="subdued">
              Track warehouse staff performance: picks per hour, items picked,
              time per order, and error rates.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Delivery Cost Report</Text>
            <Text as="p" tone="subdued">
              Analyse delivery expenses: fuel, salaries, vehicle costs. See cost per delivery
              and trends over time. (See Costs & Analytics section for details.)
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Error Analytics</Text>
            <Text as="p" tone="subdued">
              Track picking errors by type and by picker. Identify training needs
              and quality improvement opportunities. (See Costs & Analytics section.)
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Profit Analysis</Text>
            <Text as="p" tone="subdued">
              Calculate profit margins by product, vendor, or category using cost prices
              and selling prices.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Exporting Data</Text>
        <Text as="p" tone="subdued">
          All reports can be exported to CSV for further analysis:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li>Click the "Export" button on any report</li>
          <li>Configure date range and filters</li>
          <li>Download immediately or receive via email for large exports</li>
          <li>Access previous exports from Reports → Export History</li>
        </ul>
      </BlockStack>
    ),
  },
  {
    id: 'settings',
    title: 'Settings (Admin)',
    icon: SettingsIcon,
    content: (
      <BlockStack gap="400">
        <Text as="h2" variant="headingLg">Settings</Text>
        <InlineStack gap="200">
          <Icon source={AlertCircleIcon} tone="warning" />
          <Text as="p" tone="caution">
            Settings are only accessible to organisation owners and administrators.
          </Text>
        </InlineStack>

        <Divider />

        <Text as="h3" variant="headingMd">Store Management</Text>
        <Text as="p" tone="subdued">
          Manage your connected Shopify stores:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li>View all connected stores</li>
          <li>Connect additional stores (plan permitting)</li>
          <li>Disconnect stores (removes all synced data)</li>
          <li>View connection status</li>
        </ul>

        <Divider />

        <Text as="h3" variant="headingMd">General Settings</Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Delivery Date Settings</Text>
            <Text as="p" tone="subdued">
              Default lead time, maximum advance booking, timezone, weekend availability.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Date Picker Display</Text>
            <Text as="p" tone="subdued">
              Customise how the date picker appears to customers: calendar, dropdown, or buttons.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Warehouse Settings</Text>
            <Text as="p" tone="subdued">
              Auto-allocation, FIFO, overselling rules, low stock thresholds.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Notifications</Text>
            <Text as="p" tone="subdued">
              Email notification preferences for alerts and updates.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Subscription & Billing</Text>
        <Text as="p" tone="subdued">
          Manage your Whenly subscription:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li>View current plan and status</li>
          <li>Monitor usage against plan limits</li>
          <li>Update billing details</li>
          <li>Access Stripe billing portal for invoices and payment methods</li>
        </ul>

        <Divider />

        <Text as="h3" variant="headingMd">Advanced Settings</Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Data Status</Text>
            <Text as="p" tone="subdued">
              View sync statistics: products synced, orders synced, last sync time.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Webhooks</Text>
            <Text as="p" tone="subdued">
              Use Check Shopify updates to review granted access, saved incoming updates and older store subscriptions. Shared subscriptions are managed by the published Whenly app.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Payment Status Import Filter</Text>
            <Text as="p" tone="subdued">
              Configure which Shopify order payment statuses should be imported into Whenly.
              By default, only "Paid", "Partially Paid", and "Partially Refunded" orders are imported.
              Unpaid orders (Pending, Authorized) are excluded to prevent importing orders that
              may never be completed.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Import Payment Statuses</Text>
        <Text as="p" tone="subdued">
          Control which orders are imported based on their payment status:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li><strong>Paid:</strong> Fully paid orders (recommended)</li>
          <li><strong>Partially Paid:</strong> Orders with partial payment received</li>
          <li><strong>Partially Refunded:</strong> Orders with partial refunds processed</li>
          <li><strong>Pending:</strong> Orders awaiting payment (optional - may include abandoned checkouts)</li>
          <li><strong>Authorized:</strong> Payment authorized but not captured (optional)</li>
          <li><strong>Refunded:</strong> Fully refunded orders (usually not needed)</li>
        </ul>
        <Text as="p" tone="subdued">
          Configure this in Settings → Sync Settings → Import Payment Statuses.
        </Text>
      </BlockStack>
    ),
  },
  {
    id: 'team',
    title: 'Team & Permissions',
    icon: SettingsIcon,
    content: (
      <BlockStack gap="400">
        <Text as="h2" variant="headingLg">Team & Permissions</Text>
        <Text as="p">
          Invite team members and control what they can access.
        </Text>

        <Text as="h3" variant="headingMd">User Roles</Text>
        <BlockStack gap="300">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Owner</Text>
            <Text as="p" tone="subdued">
              Full access to everything. Can manage billing, delete the organisation, and manage other admins.
              There is always exactly one owner.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Admin</Text>
            <Text as="p" tone="subdued">
              Full access to all features except billing management and organisation deletion.
              Can invite and manage team members.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Manager</Text>
            <Text as="p" tone="subdued">
              Can manage orders, products, inventory, and delivery routes.
              Cannot access settings or team management.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Picker</Text>
            <Text as="p" tone="subdued">
              Limited to warehouse operations: pick lists, stock takes, and inventory adjustments.
              Cannot view orders or customer data.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Viewer</Text>
            <Text as="p" tone="subdued">
              Read-only access to orders, products, and reports. Cannot make any changes.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Inviting Team Members</Text>
        <ol style={{ marginLeft: '20px', color: '#637381' }}>
          <li>Go to Settings → Roles & Permissions</li>
          <li>Click "Invite Team Member"</li>
          <li>Enter their email address</li>
          <li>Select a role</li>
          <li>They'll receive an email invitation to join</li>
        </ol>

        <Divider />

        <Text as="h3" variant="headingMd">Custom Roles</Text>
        <Text as="p" tone="subdued">
          Use the Role Builder to create custom roles with specific permissions:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li>Choose which sections they can access</li>
          <li>Set view-only or full access per section</li>
          <li>Save and assign to team members</li>
        </ul>
      </BlockStack>
    ),
  },
  {
    id: 'warehouse-operations',
    title: 'Warehouse Operations',
    icon: InventoryIcon,
    content: (
      <BlockStack gap="400">
        <Text as="h2" variant="headingLg">Warehouse Operations</Text>
        <Text as="p">
          Advanced warehouse management features for efficient stock handling and traceability.
        </Text>

        <Text as="h3" variant="headingMd">Dynamic Slotting</Text>
        <Text as="p" tone="subdued">
          Optimise bin placement based on product velocity. Fast-moving items are suggested
          for more accessible locations.
        </Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Velocity Tracking</Text>
            <Text as="p" tone="subdued">
              The system automatically tracks how often each product is picked over a 30-day period
              and calculates a velocity score (fast, medium, slow).
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Slotting Suggestions</Text>
            <Text as="p" tone="subdued">
              Based on velocity analysis, the system suggests moving products to optimal locations.
              Accept suggestions to create transfer tasks, or reject with a reason.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">How to Use</Text>
            <Text as="p" tone="subdued">
              Navigate to Warehouse → Dynamic Slotting to view suggestions. Click "Generate Suggestions"
              to analyse your inventory and create new recommendations.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Put-Away Verification</Text>
        <Text as="p" tone="subdued">
          Ensure received stock is placed in the correct bins with barcode verification.
        </Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Put-Away Tasks</Text>
            <Text as="p" tone="subdued">
              When stock is received, put-away tasks are automatically created. Each task shows
              the product, quantity, and suggested bin location.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Verification Process</Text>
            <Text as="p" tone="subdued">
              Scan the product barcode, then scan the bin location barcode. The system verifies
              both match before completing the task. This reduces placement errors.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Task Assignment</Text>
            <Text as="p" tone="subdued">
              Tasks can be assigned to specific warehouse staff. View all tasks at
              Warehouse → Put-Away Tasks.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Serial Number Tracking</Text>
        <Text as="p" tone="subdued">
          Track individual units by serial number throughout their lifecycle.
        </Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Recording Serial Numbers</Text>
            <Text as="p" tone="subdued">
              When receiving inventory, you can record serial numbers for each unit.
              This is especially useful for electronics, appliances, and high-value items.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Tracking Lifecycle</Text>
            <Text as="p" tone="subdued">
              View the complete history of each serial number: when it was received,
              which order it was sold to, if it was returned, and its current status.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Status Types</Text>
            <Text as="p" tone="subdued">
              Serial numbers can be: Available, Allocated, Sold, Returned, or Written Off.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Damage Reports</Text>
        <Text as="p" tone="subdued">
          Document and manage damaged goods with a structured workflow.
        </Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Creating a Report</Text>
            <Text as="p" tone="subdued">
              Go to Warehouse → Damage Reports → New Report. Select the product, quantity,
              damage type (packaging, product, cosmetic, functional), and severity.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Photo Documentation</Text>
            <Text as="p" tone="subdued">
              Upload photos of the damage for documentation and potential supplier claims.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Resolution Options</Text>
            <Text as="p" tone="subdued">
              Resolve damage reports by: Write Off (remove from inventory), Repair,
              Supplier Return, or Discount Sale.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Transfer Orders</Text>
        <Text as="p" tone="subdued">
          Move stock between warehouses with full tracking and Shopify sync.
        </Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Creating a Transfer</Text>
            <Text as="p" tone="subdued">
              Go to Warehouse → Transfer Orders → New Transfer. Select source and destination
              warehouses, then add products and quantities to transfer.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Transfer Workflow</Text>
            <Text as="p" tone="subdued">
              Transfers follow a workflow: Draft → Pending → In Transit → Received.
              Stock is deducted from source when shipped and added to destination when received.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Shopify Sync</Text>
            <Text as="p" tone="subdued">
              Once received, click "Sync to Shopify" to update inventory levels in your
              Shopify locations.
            </Text>
          </BlockStack>
        </BlockStack>
      </BlockStack>
    ),
  },
  {
    id: 'automation',
    title: 'Automation & Rules',
    icon: AutomationIcon,
    content: (
      <BlockStack gap="400">
        <Text as="h2" variant="headingLg">Automation & Rules</Text>
        <Text as="p">
          Automate repetitive tasks and combine orders with intelligent rules.
        </Text>

        <Text as="h3" variant="headingMd">Order Merge Rules</Text>
        <Text as="p" tone="subdued">
          Automatically combine multiple orders from the same customer into a single delivery.
        </Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Creating Merge Rules</Text>
            <Text as="p" tone="subdued">
              Go to Orders → Merge Rules to create rules. Define conditions like: same customer email,
              same phone number, same delivery date, or same delivery zone.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Merge Conditions</Text>
            <Text as="p" tone="subdued">
              Configure how many days apart orders can be to qualify for merging.
              For example, merge orders placed within 3 days of each other.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Finding Mergeable Orders</Text>
            <Text as="p" tone="subdued">
              The system identifies orders that match your rules. Review suggested merges
              before confirming to combine them.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Automation Rules Engine</Text>
        <Text as="p" tone="subdued">
          Create conditional rules that trigger actions automatically when events occur.
        </Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Trigger Events</Text>
            <Text as="p" tone="subdued">
              Rules can trigger on: Order Created, Order Allocated, Order Picked, Stock Low,
              and more events throughout the system.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Conditions</Text>
            <Text as="p" tone="subdued">
              Set conditions that must be met: product tags contain "fragile", order total
              exceeds a value, customer is VIP, etc.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Actions</Text>
            <Text as="p" tone="subdued">
              When conditions match, execute actions: set priority, add tags, set status,
              send notifications, or trigger webhooks.
            </Text>
          </BlockStack>
        </BlockStack>

        <Text as="h4" variant="headingSm">Example Rules</Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li><strong>Fragile handling:</strong> When order contains "fragile" tag → Set priority to high</li>
          <li><strong>VIP orders:</strong> When customer has VIP tag → Add "priority-pick" tag to order</li>
          <li><strong>Low stock alert:</strong> When stock falls below 10 → Notify purchasing team</li>
          <li><strong>Large orders:</strong> When order has more than 20 items → Set packing status to "needs review"</li>
        </ul>

        <Divider />

        <Text as="h3" variant="headingMd">Order Timeline / Audit Log</Text>
        <Text as="p" tone="subdued">
          Complete history of who did what to each order.
        </Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Event Tracking</Text>
            <Text as="p" tone="subdued">
              Every action on an order is logged: created, allocated, items picked,
              packed, dispatched, delivered, returned.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Actor Information</Text>
            <Text as="p" tone="subdued">
              Each event shows who performed it (user, system, or webhook), when it happened,
              and relevant details.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Viewing the Timeline</Text>
            <Text as="p" tone="subdued">
              Open any order and scroll to the Timeline section to see the complete history.
              This is invaluable for investigating issues.
            </Text>
          </BlockStack>
        </BlockStack>
      </BlockStack>
    ),
  },
  {
    id: 'barcodes',
    title: 'Barcodes & Printing',
    icon: CodeIcon,
    content: (
      <BlockStack gap="400">
        <Text as="h2" variant="headingLg">Barcodes & Printing</Text>
        <Text as="p">
          Generate barcodes and print labels, delivery notes, and shipping documents.
        </Text>

        <Text as="h3" variant="headingMd">Barcode Generation</Text>
        <Text as="p" tone="subdued">
          Generate barcodes for products, bin locations, and orders.
        </Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Supported Formats</Text>
            <Text as="p" tone="subdued">
              Generate Code-128 (general purpose), QR codes (scannable with phones),
              and UPC barcodes (standard retail).
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Product Barcodes</Text>
            <Text as="p" tone="subdued">
              Generate barcodes from SKUs or existing product barcodes.
              Useful for labelling items that arrive without barcodes.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Location Barcodes</Text>
            <Text as="p" tone="subdued">
              Create barcodes for bin locations in your warehouse.
              Scan these during put-away and picking for verification.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Order Barcodes</Text>
            <Text as="p" tone="subdued">
              Generate barcodes for order numbers. Scan to quickly pull up order details
              during packing or delivery.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Label Printing</Text>
        <Text as="p" tone="subdued">
          Print barcode labels on thermal printers or standard printers.
        </Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Label Templates</Text>
            <Text as="p" tone="subdued">
              Choose from pre-configured templates for common label sizes:
              4x6 shipping labels, 2x1 product labels, bin location labels.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Batch Printing</Text>
            <Text as="p" tone="subdued">
              Print multiple labels at once. Select products or locations
              and generate a print job for all of them.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Delivery Notes</Text>
        <Text as="p" tone="subdued">
          Generate professional delivery notes (packing slips) to include with shipments.
        </Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Note Contents</Text>
            <Text as="p" tone="subdued">
              Delivery notes include: order number, date, customer name, delivery address,
              list of items with SKU and quantity, and special instructions.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">QR Code Tracking</Text>
            <Text as="p" tone="subdued">
              When an order has a tracking link, its delivery note includes a QR code the recipient can scan.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Generating Notes</Text>
            <Text as="p" tone="subdued">
              Open the order, choose "Print delivery note", then "Print or save PDF".
              Choose "Save a copy in Whenly" to keep that version with the order. Reopen it from the Version list.
              For a van run, open Routes, choose the route and select "Print route manifest".
              The stop list comes first, in driving order. Each stop with goods due has its own delivery note.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Sound Cues (Warehouse App)</Text>
        <Text as="p" tone="subdued">
          The warehouse mobile app provides audio feedback during scanning operations.
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li><strong>Success sound:</strong> High beep when barcode scan is verified</li>
          <li><strong>Error sound:</strong> Low buzz when scan doesn't match expected value</li>
          <li><strong>Warning sound:</strong> Medium tone for attention-needed situations</li>
        </ul>
        <Text as="p" tone="subdued">
          Sound cues help warehouse staff work efficiently without constantly looking at the screen.
        </Text>
      </BlockStack>
    ),
  },
  {
    id: 'international',
    title: 'International Shipping',
    icon: GlobeIcon,
    content: (
      <BlockStack gap="400">
        <Text as="h2" variant="headingLg">International Shipping</Text>
        <Text as="p">
          Generate customs documentation for international shipments.
        </Text>

        <Text as="h3" variant="headingMd">International Documents</Text>
        <Text as="p" tone="subdued">
          Generate required paperwork for shipping across borders.
        </Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Commercial Invoice</Text>
            <Text as="p" tone="subdued">
              Required for customs clearance. Includes itemised list with descriptions,
              quantities, values, and HS codes.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Certificate of Origin</Text>
            <Text as="p" tone="subdued">
              Document declaring where products were manufactured or produced.
              Required for some trade agreements and tariff reductions.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">CN22 / CN23 Forms</Text>
            <Text as="p" tone="subdued">
              Postal customs declarations. CN22 for items under €300, CN23 for higher values.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Generating Documents</Text>
        <ol style={{ marginLeft: '20px', color: '#637381' }}>
          <li>Open the international order</li>
          <li>Click "International Docs" in the action bar</li>
          <li>Select the document type needed</li>
          <li>Review and edit item details, HS codes, and values</li>
          <li>Generate PDF and print or attach to shipment</li>
        </ol>

        <Divider />

        <Text as="h3" variant="headingMd">HS Codes</Text>
        <Text as="p" tone="subdued">
          Harmonised System codes classify products for customs. You can set default HS codes
          on products in the Product Settings, or enter them when generating documents.
        </Text>

        <Divider />

        <Text as="h3" variant="headingMd">Intrastat Reporting</Text>
        <Text as="p" tone="subdued">
          For EU businesses, generate Intrastat reports for goods movements between EU member states.
          Access from Reports → Intrastat (EU).
        </Text>
      </BlockStack>
    ),
  },
  {
    id: 'costs-analytics',
    title: 'Costs & Analytics',
    icon: ChartVerticalIcon,
    content: (
      <BlockStack gap="400">
        <Text as="h2" variant="headingLg">Delivery Costs & Error Analytics</Text>
        <Text as="p">
          Track operational costs and identify areas for improvement.
        </Text>

        <Text as="h3" variant="headingMd">Delivery Cost Tracking</Text>
        <Text as="p" tone="subdued">
          Record and analyse the costs associated with deliveries.
        </Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Cost Types</Text>
            <Text as="p" tone="subdued">
              Track different cost categories: fuel, driver salaries, vehicle maintenance,
              tolls, parking, and other expenses.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Logging Costs</Text>
            <Text as="p" tone="subdued">
              Go to Reports → Delivery Costs to log expenses. Associate costs with
              specific routes or record general daily costs.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Receipt Upload</Text>
            <Text as="p" tone="subdued">
              Attach receipt images for expense documentation and accounting purposes.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Cost Rates</Text>
        <Text as="p" tone="subdued">
          Configure standard rates for automatic cost calculation.
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li><strong>Per kilometre:</strong> Cost per km driven</li>
          <li><strong>Per stop:</strong> Fixed cost per delivery stop</li>
          <li><strong>Per hour:</strong> Hourly rate for driver time</li>
          <li><strong>Flat rate:</strong> Fixed daily or per-route costs</li>
        </ul>

        <Divider />

        <Text as="h3" variant="headingMd">Cost per Delivery Report</Text>
        <Text as="p" tone="subdued">
          The Delivery Cost Report (Reports → Delivery Costs) shows:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li>Total costs by category over time</li>
          <li>Average cost per delivery</li>
          <li>Cost per delivery by zone</li>
          <li>Trend analysis to identify cost increases</li>
        </ul>

        <Divider />

        <Text as="h3" variant="headingMd">Error Analytics</Text>
        <Text as="p" tone="subdued">
          Track and analyse picking errors for quality improvement and training.
        </Text>
        <BlockStack gap="200">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Error Types</Text>
            <Text as="p" tone="subdued">
              Track different error categories: wrong item picked, wrong quantity,
              missed item, wrong location, damaged during pick.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Logging Errors</Text>
            <Text as="p" tone="subdued">
              When an error is discovered (by packer, QA, or customer), log it from
              Reports → Error Analytics → Log Error.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Picker Performance</Text>
            <Text as="p" tone="subdued">
              View error rates by picker to identify training opportunities.
              The dashboard shows total picks, total errors, and error rate percentage.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Error Trends</Text>
            <Text as="p" tone="subdued">
              Analyse errors over time to see if quality is improving.
              Break down by error type to focus improvement efforts.
            </Text>
          </BlockStack>
        </BlockStack>
      </BlockStack>
    ),
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    icon: QuestionCircleIcon,
    content: (
      <BlockStack gap="400">
        <Text as="h2" variant="headingLg">Troubleshooting</Text>
        <Text as="p">
          Common issues and how to resolve them.
        </Text>

        <Text as="h3" variant="headingMd">Sync Issues</Text>
        <BlockStack gap="300">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Products not appearing</Text>
            <Text as="p" tone="subdued">
              Go to Settings → Data Import and run a manual sync. Check that the product is
              published and not archived in Shopify.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Orders not syncing in real-time</Text>
            <Text as="p" tone="subdued">
              Go to Settings → Advanced and click "Check Shopify updates". Review missing access and failed incoming work. This check does not change subscriptions or prove that all topics are delivering.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Stock levels out of sync</Text>
            <Text as="p" tone="subdued">
              Whenly tracks its own inventory separate from Shopify. Use Stock Takes to reconcile
              any discrepancies.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Delivery Issues</Text>
        <BlockStack gap="300">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">No delivery dates showing for customers</Text>
            <Text as="p" tone="subdued">
              Check: (1) Delivery zones are set up, (2) The customer's address is in a zone,
              (3) Capacity is available, (4) Dates aren't blocked.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Wrong delivery dates available</Text>
            <Text as="p" tone="subdued">
              Review your delivery rules: lead time, cutoff time, and available days.
              Check product-specific lead times override defaults.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Order Issues</Text>
        <BlockStack gap="300">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Split Order option not showing</Text>
            <Text as="p" tone="subdued">
              The "Split Order" button only appears when an order has mixed allocation status -
              some items allocated and others pending. If all items are fully allocated or
              all are pending, the order cannot be split.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Orders not importing</Text>
            <Text as="p" tone="subdued">
              Check your Import Payment Status settings in Settings → Sync Settings. By default,
              only paid orders are imported. If you need pending or authorized orders, enable
              those payment statuses.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Access Issues</Text>
        <BlockStack gap="300">
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Can't access certain pages</Text>
            <Text as="p" tone="subdued">
              Your role may not have permission. Contact your organisation admin to upgrade
              your access level.
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" fontWeight="semibold">Session expired</Text>
            <Text as="p" tone="subdued">
              Sign out and sign back in. Sessions expire after 30 days of inactivity.
            </Text>
          </BlockStack>
        </BlockStack>

        <Divider />

        <Text as="h3" variant="headingMd">Getting Help</Text>
        <Text as="p" tone="subdued">
          If you can't resolve an issue:
        </Text>
        <ul style={{ marginLeft: '20px', color: '#637381' }}>
          <li>Email support: support@whenlyapp.com</li>
          <li>Include your organisation name and a description of the issue</li>
          <li>Screenshots help us diagnose problems faster</li>
        </ul>
      </BlockStack>
    ),
  },
];

// ============================================================
// HELP PAGE COMPONENT
// ============================================================

export default function Help() {
  const [activeSection, setActiveSection] = useState('getting-started');

  const currentSection = helpSections.find(s => s.id === activeSection) || helpSections[0];

  return (
    <Page title="Help & Documentation">
      <Layout>
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd">Contents</Text>
              <Divider />
              <nav>
                {helpSections.map((section) => {
                  const SectionIcon = section.icon;
                  const isActive = activeSection === section.id;
                  return (
                    <div
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderRadius: '6px',
                        backgroundColor: isActive ? 'rgba(74, 155, 140, 0.1)' : 'transparent',
                        color: isActive ? '#4a9b8c' : '#202223',
                        fontWeight: isActive ? 600 : 400,
                        fontSize: '14px',
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = '#f6f6f7';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <Icon source={SectionIcon} tone={isActive ? 'success' : 'subdued'} />
                      {section.title}
                    </div>
                  );
                })}
              </nav>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <Box padding="400">
              {currentSection.content}
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
